import { create } from 'zustand';
import { skillsApi } from '../services/api';
import {
  ASSIGNMENT_BADGES,
  ABOUT_TYPES,
  getAssignmentStatusMyPerspective
} from '../constants/assignmentStatuses';

// Default badges - ACTION_REQUIRED and IN_PROGRESS are selected by default
const defaultBadges = [
  ASSIGNMENT_BADGES.ACTION_REQUIRED,
  ASSIGNMENT_BADGES.IN_PROGRESS,
];

// Central initial state for easy reset
const initialState = {
  assignments: [],
  employeeId: null,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  pageInfo: {
    endCursor: null,
    hasNextPage: false,
    totalCount: 0
  },
  // Filters - default badges are ACTION_REQUIRED and IN_PROGRESS
  selectedBadges: [...defaultBadges],
  selectedReviewTypes: [],
  selectedAboutTypes: [],
  // UI State
  isReturningFromDetails: false,
  showControls: true,
  // Single assignment refresh tracking
  lastViewedAssignmentId: null,
  isRefreshingSingle: false,
};

const useMyAssignmentsStore = create((set, get) => {
  // Fetch all assignments for the employee from API
  const fetchAssignments = async (employeeId) => {
    if (get().isLoading) {
      return null;
    }

    set({ isLoading: true, error: null, employeeId });

    try {
      const response = await skillsApi.getEmployeeAssignments(employeeId);

      if (response) {
        set({
          assignments: response,
          pageInfo: {
            endCursor: null,
            hasNextPage: false,
            totalCount: response.length
          },
          isLoading: false,
          error: null,
        });
        return response;
      }
      set({ isLoading: false });
      return null;
    } catch (err) {
      set({
        error: err.message || 'Failed to load assignments',
        isLoading: false,
      });
      return null;
    }
  };

  return {
    ...initialState,

    // Set employee ID
    setEmployeeId: (id) => set({ employeeId: id }),

    // UI State Actions
    setIsReturningFromDetails: (flag) => set({ isReturningFromDetails: flag }),
    setShowControls: (show) => set({ showControls: show }),

    // Single assignment tracking
    setLastViewedAssignmentId: (assignmentId) => set({ lastViewedAssignmentId: assignmentId }),

    // Refresh a single assignment by ID
    refreshAssignment: async (assignmentId, employeeId) => {
      if (!assignmentId) {
        return;
      }

      set({ isRefreshingSingle: true });

      try {
        const updatedAssignment = await skillsApi.getEmployeeAssignmentById(assignmentId, employeeId);

        if (updatedAssignment) {
          set((state) => {
            // Find the existing assignment to preserve fields that might not be in the single assignment response
            const existingAssignment = state.assignments.find(a => a.id === assignmentId);

            // Merge updated data with existing data, preferring updated values
            // Preserve full reviewers list but update the current reviewer's data
            const mergedReviewers = existingAssignment?.reviewers?.map(existingReviewer => {
              // Find updated data for this reviewer from the API response
              const updatedReviewer = updatedAssignment.reviewers?.find(r => r.id === existingReviewer.id);
              // Merge updated reviewer data if found, otherwise keep existing
              return updatedReviewer ? { ...existingReviewer, ...updatedReviewer } : existingReviewer;
            }) || updatedAssignment.reviewers;

            const mergedAssignment = {
              ...existingAssignment,
              ...updatedAssignment,
              // Use merged reviewers that preserves the full list but updates individual reviewer data
              reviewers: mergedReviewers,
              // Ensure completionProgress exists - use existing if updated doesn't have it
              completionProgress: updatedAssignment.completionProgress || existingAssignment?.completionProgress || {
                completedPercentage: 0,
                totalPercentage: 0,
              }
            };

            return {
              assignments: state.assignments.map(a =>
                a.id === assignmentId ? mergedAssignment : a
              ),
              lastViewedAssignmentId: null,
              isRefreshingSingle: false,
            };
          });
        } else {
          set({ isRefreshingSingle: false });
        }
      } catch (err) {
        console.error('Failed to refresh assignment:', err);
        set({
          error: err.message || 'Failed to refresh assignment',
          isRefreshingSingle: false,
        });
      }
    },

    // Toggle handlers for chip-based filters
    toggleBadge: (badge) => {
      const { selectedBadges } = get();
      if (selectedBadges.includes(badge)) {
        set({ selectedBadges: selectedBadges.filter(b => b !== badge) });
      } else {
        set({ selectedBadges: [...selectedBadges, badge] });
      }
    },

    toggleReviewType: (reviewType) => {
      const { selectedReviewTypes } = get();
      if (selectedReviewTypes.includes(reviewType)) {
        set({ selectedReviewTypes: selectedReviewTypes.filter(rt => rt !== reviewType) });
      } else {
        set({ selectedReviewTypes: [...selectedReviewTypes, reviewType] });
      }
    },

    toggleAboutType: (aboutType) => {
      const { selectedAboutTypes } = get();
      if (selectedAboutTypes.includes(aboutType)) {
        set({ selectedAboutTypes: selectedAboutTypes.filter(at => at !== aboutType) });
      } else {
        set({ selectedAboutTypes: [...selectedAboutTypes, aboutType] });
      }
    },

    // Data actions
    fetchAssignmentsWithReset: (employeeId) => fetchAssignments(employeeId),

    // No-op since API doesn't support pagination
    fetchAssignmentsLoadMore: () => Promise.resolve(null),

    // Filters reset - restore to defaults (not empty)
    resetFilters: () => {
      set({
        selectedBadges: [...defaultBadges],
        selectedReviewTypes: [],
        selectedAboutTypes: [],
      });
    },

    // State reset
    resetState: () => set({
      ...initialState,
    }),

    // Computed: get filtered assignments
    getFilteredAssignments: () => {
      const { assignments, selectedBadges, selectedReviewTypes, selectedAboutTypes, employeeId } = get();

      return assignments.filter(assignment => {
        if (!assignment || !assignment.reviewers) {
          return false;
        }

        // Find the reviewer for the current employee
        const myReviewer = assignment.reviewers.find(reviewer => {
          return reviewer?.employee?.id === employeeId;
        });

        if (!myReviewer) {
          return false;
        }

        // Badge filter (user perspective status)
        let badgeMatch = true;
        if (selectedBadges.length > 0) {
          const assignmentBadge = getAssignmentStatusMyPerspective(assignment, employeeId);
          badgeMatch = selectedBadges.includes(assignmentBadge);
        }

        // Review type filter
        let reviewTypeMatch = true;
        if (selectedReviewTypes.length > 0) {
          reviewTypeMatch = selectedReviewTypes.includes(myReviewer.reviewType);
        }

        // About type filter (About Me vs About Others)
        let aboutTypeMatch = true;
        if (selectedAboutTypes.length > 0) {
          // "About Me" = I am the employee being assessed
          // "About Others" = I am assessing someone else
          const isAboutMe = assignment.employee?.id === employeeId;
          const aboutType = isAboutMe ? ABOUT_TYPES.ABOUT_ME : ABOUT_TYPES.ABOUT_OTHERS;
          aboutTypeMatch = selectedAboutTypes.includes(aboutType);
        }

        return badgeMatch && reviewTypeMatch && aboutTypeMatch;
      });
    },
  };
});

export default useMyAssignmentsStore;
